import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getTestsCount } from '../../utils/testUtils';
import './ConflictResolutionModal.css';

/**
 * 🎨 MODAL UNIVERSELLE DE RÉSOLUTION DE CONFLITS AVEC MERGE INTERACTIF
 * 
 * Affiche une comparaison visuelle entre le nœud existant et les nouvelles données
 * Permet de choisir champ par champ quelle version garder (comme git merge)
 * Propose trois actions : Merge personnalisé, Enrichir automatique, Créer distinct
 * 
 * @param {Object} props
 * @param {Object} props.conflict - Données du conflit détecté
 * @param {Function} props.onResolve - Callback de résolution (action, existingNode, newData, mergedData)
 * @param {Function} props.onCancel - Callback d'annulation
 * @param {boolean} props.isResolving - Indique si la résolution est en cours
 */
export function ConflictResolutionModal({ conflict, onResolve, onCancel, isResolving }) {
  const { t } = useTranslation();

  if (!conflict) return null;

  // ✅ Valeurs par défaut pour éviter les undefined
  const {
    conflictType = 'unknown',
    existingNode = { name: 'Unknown', type: 'Unknown' }, // 🔥 Valeur par défaut
    newData = { name: 'Unknown', type: 'Unknown' }, // 🔥 Valeur par défaut
    similarity = 0,
    recommendation = 'review',
    detailedScores = {},
    explanation = '',
    isMultiple = false,
    currentIndex = 0,
    totalConflicts = 1
  } = conflict;

  // 🛡️ Log d'avertissement si données manquantes
  if (!conflict.existingNode || !conflict.newData) {
    console.warn('⚠️ [ConflictModal] Données de conflit incomplètes:', conflict);
  }

  // 🎯 État pour gérer les choix de merge par champ
  // Valeurs possibles : 'existing' | 'new' | 'both' | 'manual'
  const [fieldChoices, setFieldChoices] = useState({
    name: 'existing', // Par défaut on garde le nom existant
    type: 'existing',
    technology: 'existing',
    featureTeam: 'new', // Par défaut on prend la nouvelle team
    description: 'both' // Par défaut on fusionne les descriptions
  });

  // État pour les éditions manuelles
  const [manualEdits, setManualEdits] = useState({});

  // Mode de la modal : 'compare' (vue classique) ou 'merge' (vue merge interactive)
  const [viewMode, setViewMode] = useState('compare');

  // Calcul des données fusionnées en temps réel
  const mergedData = useMemo(() => {
    // ✅ Protection contre existingNode ou newData undefined
    if (!existingNode || !newData) {
      console.warn('[ConflictModal] existingNode ou newData manquant:', { existingNode, newData });
      return {};
    }
    
    const result = {};
    
    Object.keys(fieldChoices).forEach(field => {
      const choice = fieldChoices[field];
      const existingValue = existingNode[field];
      const newValue = newData[field];

      switch (choice) {
        case 'existing':
          result[field] = existingValue;
          break;
        case 'new':
          result[field] = newValue;
          break;
        case 'both':
          // Fusion intelligente selon le type de champ
          if (field === 'description') {
            result[field] = `${existingValue || ''}\n\n${newValue || ''}`.trim();
          } else if (field === 'technology' || field === 'featureTeam') {
            result[field] = [existingValue, newValue].filter(Boolean).join(' + ');
          } else {
            result[field] = newValue || existingValue;
          }
          break;
        case 'manual':
          result[field] = manualEdits[field] || existingValue;
          break;
        default:
          result[field] = existingValue;
      }
    });

    return result;
  }, [fieldChoices, manualEdits, existingNode, newData]);

  // Gestion des actions
  const handleResolve = (action) => {
    if (action === 'merge') {
      // Merge personnalisé avec les choix de l'utilisateur
      onResolve({
        action: 'merge',
        existingNode,
        newData,
        mergedData
      });
    } else {
      // Actions classiques
      onResolve({
        action, // 'enrich' ou 'create'
        existingNode,
        newData,
      });
    }
  };

  // Changer le choix pour un champ
  const handleFieldChoice = (field, choice) => {
    setFieldChoices(prev => ({
      ...prev,
      [field]: choice
    }));
  };

  // Édition manuelle d'un champ
  const handleManualEdit = (field, value) => {
    setManualEdits(prev => ({
      ...prev,
      [field]: value
    }));
    handleFieldChoice(field, 'manual');
  };

  // 🎯 NOUVEAU : Toggle rapide en cliquant sur une valeur (existant <-> nouveau)
  const handleQuickToggle = (field, currentChoice) => {
    if (currentChoice === 'existing') {
      handleFieldChoice(field, 'new');
    } else if (currentChoice === 'new') {
      handleFieldChoice(field, 'existing');
    } else {
      // Si c'est 'both' ou 'manual', passer à 'existing'
      handleFieldChoice(field, 'existing');
    }
  };

  // 🎯 NOUVEAU : Édition directe dans la prévisualisation
  const handlePreviewEdit = (field, value) => {
    setManualEdits(prev => ({
      ...prev,
      [field]: value
    }));
    handleFieldChoice(field, 'manual');
  };

  // Fonction pour afficher les scores détaillés
  const renderDetailedScores = () => {
    if (!detailedScores) return null;

    const scoreItems = [
      { label: t('conflict.scores.name'), value: detailedScores.name },
      { label: t('conflict.scores.type'), value: detailedScores.type },
      { label: t('conflict.scores.technology'), value: detailedScores.technology },
      { label: t('conflict.scores.featureTeam'), value: detailedScores.featureTeam },
      { label: t('conflict.scores.description'), value: detailedScores.description },
    ];

    return (
      <details className="detailed-scores">
        <summary>📊 {t('conflict.detailedScores')}</summary>
        <ul>
          {scoreItems.map((item, index) => (
            <li key={index}>
              <span className="score-label">{item.label}:</span>
              <div className="score-bar-container">
                <div 
                  className="score-bar" 
                  style={{ width: `${item.value * 100}%` }}
                />
                <span className="score-value">{Math.round(item.value * 100)}%</span>
              </div>
            </li>
          ))}
        </ul>
      </details>
    );
  };

  return (
    <div className="crm-overlay" onClick={onCancel}>
      <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
        
        {/* 🎯 En-tête */}
        <div className="crm-header">
          <div className={`crm-type-badge ${conflictType}`}>
            {conflictType === 'exact' && '⚠️ '}
            {conflictType === 'contextual' && '💡 '}
            {conflictType === 'fuzzy' && '🔍 '}
            {t(`conflict.types.${conflictType}`)}
          </div>
          <h2 className="crm-title">
            {t('conflict.title') || 'Nœud similaire détecté'}
            {isMultiple && (
              <span style={{ fontSize: '0.7em', opacity: 0.8, marginLeft: '12px' }}>
                ({currentIndex + 1}/{totalConflicts})
              </span>
            )}
          </h2>
          <p className="crm-subtitle">
            {t('conflict.similarity')}: {Math.round(similarity * 100)}%
            {isMultiple && (
              <span style={{ marginLeft: '16px', opacity: 0.9 }}>
                • {totalConflicts - currentIndex - 1} {t('conflict.remaining') || 'restant(s)'}
              </span>
            )}
          </p>
        </div>

        {/* 📊 Body */}
        <div className="crm-body">
          {/* Similarity bar */}
          <div className="crm-similarity-section">
            <div className="crm-similarity-label">
              🎯 {t('conflict.similarity') || 'Similarité'}
            </div>
            <div className="crm-similarity-bar">
              <div 
                className="crm-similarity-fill" 
                style={{ width: `${Math.round(similarity * 100)}%` }}
              >
                {Math.round(similarity * 100)}%
              </div>
            </div>
            {explanation && (
              <div className="crm-explanation">{explanation}</div>
            )}
          </div>

          {/* Toggle view mode */}
          <div className="crm-view-toggle">
            <button 
              className={`crm-toggle-btn ${viewMode === 'compare' ? 'active' : ''}`}
              onClick={() => setViewMode('compare')}
            >
              👁️ {t('conflict.views.compare') || 'Vue Comparaison'}
            </button>
            <button 
              className={`crm-toggle-btn ${viewMode === 'merge' ? 'active' : ''}`}
              onClick={() => setViewMode('merge')}
            >
              🔀 {t('conflict.views.merge') || 'Merge Interactif'}
            </button>
          </div>

          {/* VUE COMPARAISON CLASSIQUE */}
          {viewMode === 'compare' && (
            <div className="crm-comparison">
              
              {/* Nœud existant */}
              <div className="crm-node-card existing">
                <div className="crm-card-header">
                  <span className="crm-card-icon">📦</span>
                  <h3 className="crm-card-title">{t('conflict.existingNode') || 'Nœud existant'}</h3>
                </div>
                
                <div className="crm-field">
                  <div className="crm-field-label">{t('common.name') || 'Nom'}:</div>
                  <div className="crm-field-value">{existingNode.name}</div>
                </div>

                <div className="crm-field">
                  <div className="crm-field-label">{t('common.type') || 'Type'}:</div>
                  <div className="crm-field-value">{existingNode.type}</div>
                </div>

                {existingNode.technology && (
                  <div className="crm-field">
                    <div className="crm-field-label">{t('common.technology') || 'Technology'}:</div>
                    <div className="crm-field-value">{existingNode.technology}</div>
                  </div>
                )}

                {existingNode.featureTeam && (
                  <div className="crm-field">
                    <div className="crm-field-label">{t('common.featureTeam') || 'Feature Team'}:</div>
                    <div className="crm-field-value">{existingNode.featureTeam}</div>
                  </div>
                )}

                {existingNode.description && (
                  <div className="crm-field">
                    <div className="crm-field-label">{t('common.description') || 'Description'}:</div>
                    <div className="crm-field-value description">{existingNode.description}</div>
                  </div>
                )}
              </div>

              {/* Arrow */}
              <div className="crm-arrow">↔️</div>

              {/* Nouveau nœud */}
              <div className="crm-node-card proposed">
                <div className="crm-card-header">
                  <span className="crm-card-icon">✨</span>
                  <h3 className="crm-card-title">{t('conflict.proposedData') || 'Nouveau nœud'}</h3>
                </div>
                
                <div className="crm-field">
                  <div className="crm-field-label">{t('common.name') || 'Nom'}:</div>
                  <div className="crm-field-value">{newData.name}</div>
                </div>

                <div className="crm-field">
                  <div className="crm-field-label">{t('common.type') || 'Type'}:</div>
                  <div className="crm-field-value">{newData.type}</div>
                </div>

                {newData.technology && (
                  <div className="crm-field">
                    <div className="crm-field-label">{t('common.technology') || 'Technology'}:</div>
                    <div className="crm-field-value">{newData.technology}</div>
                  </div>
                )}

                {newData.featureTeam && (
                  <div className="crm-field">
                    <div className="crm-field-label">{t('common.featureTeam') || 'Feature Team'}:</div>
                    <div className="crm-field-value">{newData.featureTeam}</div>
                  </div>
                )}

                {newData.description && (
                  <div className="crm-field">
                    <div className="crm-field-label">{t('common.description') || 'Description'}:</div>
                    <div className="crm-field-value description">{newData.description}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* VUE MERGE INTERACTIF */}
          {viewMode === 'merge' && (
            <div className="crm-merge-view">
              <div className="crm-merge-instructions">
                💡 {t('conflict.merge.instructions') || 'Sélectionnez la version à conserver pour chaque champ, ou fusionnez les deux.'}
              </div>

              {/* Champ Name */}
              <div className="crm-merge-field">
                <div className="crm-merge-header">
                  <strong>🏷️ {t('common.name') || 'Nom'}</strong>
                  <span className="crm-field-score">
                    {t('conflict.similarity') || 'Similarité'}: {Math.round((detailedScores?.name || 0) * 100)}%
                  </span>
                </div>
                <div className="crm-merge-options">
                  <div 
                    className={`crm-merge-option ${fieldChoices.name === 'existing' ? 'selected' : ''}`}
                    onClick={() => handleFieldChoice('name', 'existing')}
                  >
                    <input 
                      type="radio" 
                      name="name" 
                      checked={fieldChoices.name === 'existing'}
                      readOnly
                    />
                    <div className="crm-option-content">
                      <span className="crm-option-label">📦 {t('conflict.merge.existing') || 'Existant'}</span>
                      <span 
                        className="crm-option-value crm-clickable-value"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuickToggle('name', fieldChoices.name);
                        }}
                        title={t('conflict.merge.clickToToggle') || 'Cliquez pour basculer'}
                      >
                        {existingNode.name}
                      </span>
                    </div>
                  </div>
                  <div 
                    className={`crm-merge-option ${fieldChoices.name === 'new' ? 'selected' : ''}`}
                    onClick={() => handleFieldChoice('name', 'new')}
                  >
                    <input 
                      type="radio" 
                      name="name" 
                      checked={fieldChoices.name === 'new'}
                      readOnly
                    />
                    <div className="crm-option-content">
                      <span className="crm-option-label">✨ {t('conflict.merge.new') || 'Nouveau'}</span>
                      <span 
                        className="crm-option-value crm-clickable-value"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuickToggle('name', fieldChoices.name);
                        }}
                        title={t('conflict.merge.clickToToggle') || 'Cliquez pour basculer'}
                      >
                        {newData.name}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Champ Type */}
              <div className="crm-merge-field">
                <div className="crm-merge-header">
                  <strong>📂 {t('common.type') || 'Type'}</strong>
                  <span className="crm-field-score">
                    {t('conflict.similarity') || 'Similarité'}: {Math.round((detailedScores?.type || 0) * 100)}%
                  </span>
                </div>
                <div className="crm-merge-options">
                  <div 
                    className={`crm-merge-option ${fieldChoices.type === 'existing' ? 'selected' : ''}`}
                    onClick={() => handleFieldChoice('type', 'existing')}
                  >
                    <input 
                      type="radio" 
                      name="type" 
                      checked={fieldChoices.type === 'existing'}
                      readOnly
                    />
                    <div className="crm-option-content">
                      <span className="crm-option-label">📦 {t('conflict.merge.existing') || 'Existant'}</span>
                      <span 
                        className="crm-option-value crm-clickable-value"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuickToggle('type', fieldChoices.type);
                        }}
                        title={t('conflict.merge.clickToToggle') || 'Cliquez pour basculer'}
                      >
                        {existingNode.type}
                      </span>
                    </div>
                  </div>
                  <div 
                    className={`crm-merge-option ${fieldChoices.type === 'new' ? 'selected' : ''}`}
                    onClick={() => handleFieldChoice('type', 'new')}
                  >
                    <input 
                      type="radio" 
                      name="type" 
                      checked={fieldChoices.type === 'new'}
                      readOnly
                    />
                    <div className="crm-option-content">
                      <span className="crm-option-label">✨ {t('conflict.merge.new') || 'Nouveau'}</span>
                      <span 
                        className="crm-option-value crm-clickable-value"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuickToggle('type', fieldChoices.type);
                        }}
                        title={t('conflict.merge.clickToToggle') || 'Cliquez pour basculer'}
                      >
                        {newData.type}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Champ Technology */}
              {(existingNode.technology || newData.technology) && (
                <div className="crm-merge-field">
                  <div className="crm-merge-header">
                    <strong>⚙️ {t('common.technology') || 'Technologie'}</strong>
                    <span className="crm-field-score">
                      {t('conflict.similarity') || 'Similarité'}: {Math.round((detailedScores?.technology || 0) * 100)}%
                    </span>
                  </div>
                  <div className="crm-merge-options">
                    <div 
                      className={`crm-merge-option ${fieldChoices.technology === 'existing' ? 'selected' : ''}`}
                      onClick={() => handleFieldChoice('technology', 'existing')}
                    >
                      <input 
                        type="radio" 
                        name="technology" 
                        checked={fieldChoices.technology === 'existing'}
                        readOnly
                      />
                      <div className="crm-option-content">
                        <span className="crm-option-label">📦 {t('conflict.merge.existing') || 'Existant'}</span>
                        <span 
                          className="crm-option-value crm-clickable-value"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickToggle('technology', fieldChoices.technology);
                          }}
                          title={t('conflict.merge.clickToToggle') || 'Cliquez pour basculer'}
                        >
                          {existingNode.technology || `(${t('common.empty') || 'vide'})`}
                        </span>
                      </div>
                    </div>
                    <div 
                      className={`crm-merge-option ${fieldChoices.technology === 'new' ? 'selected' : ''}`}
                      onClick={() => handleFieldChoice('technology', 'new')}
                    >
                      <input 
                        type="radio" 
                        name="technology" 
                        checked={fieldChoices.technology === 'new'}
                        readOnly
                      />
                      <div className="crm-option-content">
                        <span className="crm-option-label">✨ {t('conflict.merge.new') || 'Nouveau'}</span>
                        <span 
                          className="crm-option-value crm-clickable-value"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickToggle('technology', fieldChoices.technology);
                          }}
                          title={t('conflict.merge.clickToToggle') || 'Cliquez pour basculer'}
                        >
                          {newData.technology || `(${t('common.empty') || 'vide'})`}
                        </span>
                      </div>
                    </div>
                    <div 
                      className={`crm-merge-option ${fieldChoices.technology === 'both' ? 'selected' : ''}`}
                      onClick={() => handleFieldChoice('technology', 'both')}
                    >
                      <input 
                        type="radio" 
                        name="technology" 
                        checked={fieldChoices.technology === 'both'}
                        readOnly
                      />
                      <div className="crm-option-content">
                        <span className="crm-option-label">🔀 {t('conflict.merge.mergeValues') || 'Fusionner'}</span>
                        <span className="crm-option-value">
                          {[existingNode.technology, newData.technology].filter(Boolean).join(' + ')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Champ Feature Team */}
              {(existingNode.featureTeam || newData.featureTeam) && (
                <div className="crm-merge-field">
                  <div className="crm-merge-header">
                    <strong>👥 {t('common.featureTeam') || 'Feature Team'}</strong>
                    <span className="crm-field-score">
                      {t('conflict.similarity') || 'Similarité'}: {Math.round((detailedScores?.featureTeam || 0) * 100)}%
                    </span>
                  </div>
                  <div className="crm-merge-options">
                    <div 
                      className={`crm-merge-option ${fieldChoices.featureTeam === 'existing' ? 'selected' : ''}`}
                      onClick={() => handleFieldChoice('featureTeam', 'existing')}
                    >
                      <input 
                        type="radio" 
                        name="featureTeam" 
                        checked={fieldChoices.featureTeam === 'existing'}
                        readOnly
                      />
                      <div className="crm-option-content">
                        <span className="crm-option-label">📦 {t('conflict.merge.existing') || 'Existant'}</span>
                        <span 
                          className="crm-option-value crm-clickable-value"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickToggle('featureTeam', fieldChoices.featureTeam);
                          }}
                          title={t('conflict.merge.clickToToggle') || 'Cliquez pour basculer'}
                        >
                          {existingNode.featureTeam || `(${t('common.empty') || 'vide'})`}
                        </span>
                      </div>
                    </div>
                    <div 
                      className={`crm-merge-option ${fieldChoices.featureTeam === 'new' ? 'selected' : ''}`}
                      onClick={() => handleFieldChoice('featureTeam', 'new')}
                    >
                      <input 
                        type="radio" 
                        name="featureTeam" 
                        checked={fieldChoices.featureTeam === 'new'}
                        readOnly
                      />
                      <div className="crm-option-content">
                        <span className="crm-option-label">✨ {t('conflict.merge.new') || 'Nouveau'}</span>
                        <span 
                          className="crm-option-value crm-clickable-value"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickToggle('featureTeam', fieldChoices.featureTeam);
                          }}
                          title={t('conflict.merge.clickToToggle') || 'Cliquez pour basculer'}
                        >
                          {newData.featureTeam || `(${t('common.empty') || 'vide'})`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Champ Description */}
              {(existingNode.description || newData.description) && (
                <div className="crm-merge-field">
                  <div className="crm-merge-header">
                    <strong>📝 {t('common.description') || 'Description'}</strong>
                    <span className="crm-field-score">
                      {t('conflict.similarity') || 'Similarité'}: {Math.round((detailedScores?.description || 0) * 100)}%
                    </span>
                  </div>
                  <div className="crm-merge-options">
                    <div 
                      className={`crm-merge-option ${fieldChoices.description === 'existing' ? 'selected' : ''}`}
                      onClick={() => handleFieldChoice('description', 'existing')}
                    >
                      <input 
                        type="radio" 
                        name="description" 
                        checked={fieldChoices.description === 'existing'}
                        readOnly
                      />
                      <div className="crm-option-content">
                        <span className="crm-option-label">📦 {t('conflict.merge.existing') || 'Existant'}</span>
                        <span 
                          className="crm-option-value description crm-clickable-value"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickToggle('description', fieldChoices.description);
                          }}
                          title={t('conflict.merge.clickToToggle') || 'Cliquez pour basculer'}
                        >
                          {existingNode.description || `(${t('common.empty') || 'vide'})`}
                        </span>
                      </div>
                    </div>
                    <div 
                      className={`crm-merge-option ${fieldChoices.description === 'new' ? 'selected' : ''}`}
                      onClick={() => handleFieldChoice('description', 'new')}
                    >
                      <input 
                        type="radio" 
                        name="description" 
                        checked={fieldChoices.description === 'new'}
                        readOnly
                      />
                      <div className="crm-option-content">
                        <span className="crm-option-label">✨ {t('conflict.merge.new') || 'Nouveau'}</span>
                        <span 
                          className="crm-option-value description crm-clickable-value"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickToggle('description', fieldChoices.description);
                          }}
                          title={t('conflict.merge.clickToToggle') || 'Cliquez pour basculer'}
                        >
                          {newData.description || `(${t('common.empty') || 'vide'})`}
                        </span>
                      </div>
                    </div>
                    <div 
                      className={`crm-merge-option ${fieldChoices.description === 'both' ? 'selected' : ''}`}
                      onClick={() => handleFieldChoice('description', 'both')}
                    >
                      <input 
                        type="radio" 
                        name="description" 
                        checked={fieldChoices.description === 'both'}
                        readOnly
                      />
                      <div className="crm-option-content">
                        <span className="crm-option-label">🔀 {t('conflict.merge.mergeBoth') || 'Fusionner (les deux)'}</span>
                        <span className="crm-option-value description">
                          {existingNode.description && `${existingNode.description}\n\n`}
                          {newData.description}
                        </span>
                      </div>
                    </div>
                    <div 
                      className={`crm-merge-option ${fieldChoices.description === 'manual' ? 'selected' : ''}`}
                      onClick={() => handleFieldChoice('description', 'manual')}
                    >
                      <input 
                        type="radio" 
                        name="description" 
                        checked={fieldChoices.description === 'manual'}
                        readOnly
                      />
                      <div className="crm-option-content">
                        <span className="crm-option-label">✏️ {t('conflict.merge.manualEdit') || 'Édition manuelle'}</span>
                        {fieldChoices.description === 'manual' && (
                          <textarea
                            className="crm-manual-edit"
                            value={manualEdits.description || existingNode.description || ''}
                            onChange={(e) => handleManualEdit('description', e.target.value)}
                            rows={4}
                            placeholder={t('conflict.merge.customTextPlaceholder') || 'Saisissez votre texte personnalisé...'}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 🧪 Affichage des tests */}
              {(existingNode.testsJson || newData.testsJson) && (
                <div className="crm-tests-section">
                  <div className="crm-merge-header">
                    <strong>🧪 {t('common.tests') || 'Tests'}</strong>
                    <span className="crm-tests-count">
                      {t('conflict.merge.testsTotal') || 'Total'}: {getTestsCount(existingNode.testsJson) + getTestsCount(newData.testsJson)} {t('common.tests') || 'tests'}
                    </span>
                  </div>
                  
                  <div className="crm-tests-info">
                    <div className="crm-test-card">
                      <span className="crm-test-label">📦 {t('conflict.merge.existing') || 'Nœud existant'}:</span>
                      <span className="crm-test-count">{getTestsCount(existingNode.testsJson)} {t('common.tests') || 'tests'}</span>
                    </div>
                    <div className="crm-test-arrow">→</div>
                    <div className="crm-test-card">
                      <span className="crm-test-label">✨ {t('conflict.merge.new') || 'Nouveau nœud'}:</span>
                      <span className="crm-test-count">{getTestsCount(newData.testsJson)} {t('common.tests') || 'tests'}</span>
                    </div>
                  </div>
                  
                  <div className="crm-tests-note">
                    ℹ️ {t('conflict.merge.testsNote') || 'Les tests des deux nœuds seront automatiquement fusionnés lors du merge.'}
                  </div>
                </div>
              )}

              {/* Prévisualisation du résultat ÉDITABLE */}
              <div className="crm-merge-preview">
                <div className="crm-preview-header">
                  <strong>👁️ {t('conflict.merge.previewTitle') || 'Aperçu du nœud fusionné'}</strong>
                  <span className="crm-preview-hint">💡 {t('conflict.merge.previewHint') || 'Cliquez sur un champ pour l\'éditer directement'}</span>
                </div>
                <div className="crm-preview-content">
                  
                  {/* Nom - Éditable */}
                  <div className="crm-preview-field">
                    <strong>{t('common.name') || 'Nom'}:</strong>
                    <input
                      type="text"
                      className="crm-preview-input"
                      value={manualEdits.name !== undefined ? manualEdits.name : mergedData.name}
                      onChange={(e) => handlePreviewEdit('name', e.target.value)}
                      placeholder={t('conflict.merge.namePlaceholder') || 'Nom du nœud...'}
                    />
                  </div>

                  {/* Type - Éditable */}
                  <div className="crm-preview-field">
                    <strong>{t('common.type') || 'Type'}:</strong>
                    <input
                      type="text"
                      className="crm-preview-input"
                      value={manualEdits.type !== undefined ? manualEdits.type : mergedData.type}
                      onChange={(e) => handlePreviewEdit('type', e.target.value)}
                      placeholder={t('conflict.merge.typePlaceholder') || 'Type du nœud...'}
                    />
                  </div>

                  {/* Technologie - Éditable (optionnel) */}
                  {(mergedData.technology || manualEdits.technology !== undefined) && (
                    <div className="crm-preview-field">
                      <strong>{t('common.technology') || 'Technologie'}:</strong>
                      <input
                        type="text"
                        className="crm-preview-input"
                        value={manualEdits.technology !== undefined ? manualEdits.technology : mergedData.technology}
                        onChange={(e) => handlePreviewEdit('technology', e.target.value)}
                        placeholder={t('conflict.merge.technologyPlaceholder') || 'Technologie utilisée...'}
                      />
                    </div>
                  )}

                  {/* Feature Team - Éditable (optionnel) */}
                  {(mergedData.featureTeam || manualEdits.featureTeam !== undefined) && (
                    <div className="crm-preview-field">
                      <strong>{t('common.featureTeam') || 'Feature Team'}:</strong>
                      <input
                        type="text"
                        className="crm-preview-input"
                        value={manualEdits.featureTeam !== undefined ? manualEdits.featureTeam : mergedData.featureTeam}
                        onChange={(e) => handlePreviewEdit('featureTeam', e.target.value)}
                        placeholder={t('conflict.merge.featureTeamPlaceholder') || 'Équipe responsable...'}
                      />
                    </div>
                  )}

                  {/* Description - Éditable (textarea) */}
                  {(mergedData.description || manualEdits.description !== undefined) && (
                    <div className="crm-preview-field">
                      <strong>{t('common.description') || 'Description'}:</strong>
                      <textarea
                        className="crm-preview-textarea"
                        value={manualEdits.description !== undefined ? manualEdits.description : mergedData.description}
                        onChange={(e) => handlePreviewEdit('description', e.target.value)}
                        placeholder={t('conflict.merge.descriptionPlaceholder') || 'Description du nœud...'}
                        rows={4}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="crm-footer">
          <button
            className="crm-action-btn crm-btn-cancel"
            onClick={onCancel}
            disabled={isResolving}
          >
            <span>❌ {t('common.cancel') || 'Annuler'}</span>
          </button>

          {viewMode === 'merge' ? (
            /* MODE MERGE : Appliquer le merge personnalisé */
            <button
              className="crm-action-btn crm-btn-merge"
              onClick={() => handleResolve('merge')}
              disabled={isResolving}
            >
              <span>🔀 {t('conflict.actions.applyMerge') || 'Appliquer le merge'}</span>
            </button>
          ) : (
            /* MODE COMPARE : Actions classiques */
            <>
              <button
                className="crm-action-btn crm-btn-enrich"
                onClick={() => handleResolve('enrich')}
                disabled={isResolving}
              >
                <span>🔄 {t('conflict.actions.enrich') || 'Enrichir auto'}</span>
              </button>

              <button
                className="crm-action-btn crm-btn-create"
                onClick={() => handleResolve('create')}
                disabled={isResolving}
              >
                <span>➕ {t('conflict.actions.createNew') || 'Créer distinct'}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ConflictResolutionModal;
